/**
 * AI 对话 @ 提及资源相关 API Hooks
 */
import { useCallback } from 'react';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import { aiConversationApi } from '@/services/api';
import type { MentionResourceType, MentionableResourcesResponse } from '@/types/mention';
import type { MentionRelatedResourceItem } from '@/services/api/ai-conversations';
import { resolveMentionableSubmitResourceType } from '@/lib/conversations/reference-submit-identity';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { editorMentionKeys } from '@/hooks/api/useEditorMentionCandidates';

const MENTION_QUERY_KEY = 'ai-mentions' as const;
const PERSONAL_CONTEXT_QUERY_KEY = '__personal__';

export const mentionKeys = {
  all: [MENTION_QUERY_KEY] as const,
  list: (
    activeOrgId: string | null,
    storyboardId: string,
    search?: string,
    type?: MentionResourceType
  ) =>
    [
      ...mentionKeys.all,
      storyboardId,
      activeOrgId ?? PERSONAL_CONTEXT_QUERY_KEY,
      search ?? '',
      type ?? '',
    ] as const,
};

/** 失效该分镜下所有 @ 关联查询（不限 search/type），避免 queryKey 前缀与 v5 匹配不一致 */
export function invalidateMentionQueriesForStoryboard(
  queryClient: QueryClient,
  storyboardId: string
): void {
  if (!storyboardId) return;
  void queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return Array.isArray(k) && k[0] === MENTION_QUERY_KEY && k[1] === storyboardId;
    },
  });
  void queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return Array.isArray(k) && k[0] === editorMentionKeys.all[0] && k[1] === storyboardId;
    },
  });
}

/**
 * 返回一个函数，调用后失效指定分镜下所有 @ 提及资源缓存。
 */
export function useInvalidateStoryboardMentionResources() {
  const queryClient = useQueryClient();
  return useCallback(
    (storyboardId: string) => invalidateMentionQueriesForStoryboard(queryClient, storyboardId),
    [queryClient]
  );
}

function mapRelatedTypeToMentionType(
  type: MentionRelatedResourceItem['type']
): MentionResourceType {
  if (type === 'location') return 'location';
  return type;
}

function mapRelatedResourcesToMentionable(
  storyboardId: string,
  items: MentionRelatedResourceItem[],
  search?: string,
  filterType?: MentionResourceType
): MentionableResourcesResponse {
  const keyword = search?.trim().toLowerCase();
  const resources = items
    .map((item) => {
      const mappedType = mapRelatedTypeToMentionType(item.type);
      if (filterType && mappedType !== filterType) return null;
      const submitResourceType = resolveMentionableSubmitResourceType(item.type, item.resourceType);

      const mentionResource = {
        type: mappedType,
        elementId: item.elementId,
        elementName: item.elementName,
        tagId: item.tagId ?? undefined,
        tagLabel: item.tagLabel ?? undefined,
        resourceId: item.resourceId,
        resourceUrl: item.resourceUrl ?? undefined,
        thumbnailUrl: item.thumbnailUrl || item.resourceUrl || undefined,
        mimeType: item.mimeType ?? undefined,
        submitResourceId: item.resourceId,
        submitResourceType,
        fileSize: item.fileSize ?? undefined,
        width: item.width ?? undefined,
        height: item.height ?? undefined,
        durationSeconds: item.duration ?? undefined,
        resolution: item.resolution ?? undefined,
        format: item.format ?? undefined,
        frameRate: item.frameRate ?? undefined,
        humanAssetType: item.humanAssetType ?? null,
        humanAssetStatus: item.humanAssetStatus ?? null,
        versionSeq: item.versionSeq ?? null,
      };

      if (!keyword) return mentionResource;
      const displayName = mentionResource.tagLabel
        ? `${mentionResource.elementName}-${mentionResource.tagLabel}`
        : mentionResource.elementName;
      return displayName.toLowerCase().includes(keyword) ? mentionResource : null;
    })
    .filter((resource): resource is NonNullable<typeof resource> => Boolean(resource));

  return {
    conversation: {
      conversationId: '',
      targetType: 1,
      targetId: storyboardId,
      targetName: '当前分镜',
    },
    resources,
  };
}

export function useMentionableResources(
  storyboardId: string,
  params?: { type?: MentionResourceType; search?: string },
  options?: { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);

  return useQuery<MentionableResourcesResponse>({
    queryKey: mentionKeys.list(activeOrgId, storyboardId, params?.search, params?.type),
    queryFn: async () => {
      const response = await aiConversationApi.getStoryboardRelatedResources(storyboardId, {
        activeOrgId,
      });
      return mapRelatedResourcesToMentionable(
        storyboardId,
        response.resources ?? [],
        params?.search,
        params?.type
      );
    },
    enabled: Boolean(storyboardId) && (options?.enabled ?? true),
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 10,
    placeholderData: keepPreviousData,
  });
}

/**
 * 失活 AI 输入框 mention 候选资源缓存。
 * 在修改元素标签（装扮）的定稿素材后调用，确保 mention 列表反映最新装扮。
 */
export function invalidateMentions(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: mentionKeys.all });
  void queryClient.invalidateQueries({ queryKey: editorMentionKeys.all });
}

/**
 * 标签更新成功后就地同步 @ 候选中的装扮名称，避免等待后台查询才显示新名称。
 * 仅修改命中 tagId 的 tagLabel，其余字段和未变化引用保持不变。
 */
export function syncMentionTagLabelInCache(
  queryClient: QueryClient,
  params: { tagId: string; tagLabel: string }
): void {
  const tagId = params.tagId.trim();
  const tagLabel = params.tagLabel.trim();
  if (!tagId) return;

  queryClient.setQueriesData<MentionableResourcesResponse>(
    { queryKey: mentionKeys.all },
    (current) => {
      if (!current?.resources?.length) return current;

      let changed = false;
      const resources = current.resources.map((resource) => {
        if (resource.tagId?.trim() !== tagId || resource.tagLabel?.trim() === tagLabel) {
          return resource;
        }
        changed = true;
        return { ...resource, tagLabel };
      });

      return changed ? { ...current, resources } : current;
    }
  );
}

/**
 * 就地更新 mention 候选缓存中某资源的展示名称（footage 重命名无感同步）。
 * 按 resourceId 匹配，同步 elementName（footage 展示名即 elementName），不触发重拉。
 */
export function updateMentionResourceNameInCache(
  queryClient: QueryClient,
  params: { resourceId: string; name: string }
): void {
  const normalizedId = String(params.resourceId ?? '').trim();
  if (!normalizedId) return;

  queryClient.setQueriesData<MentionableResourcesResponse>(
    {
      predicate: (query) => {
        const k = query.queryKey;
        return Array.isArray(k) && k[0] === MENTION_QUERY_KEY;
      },
    },
    (current) => {
      if (!current?.resources?.length) return current;
      let changed = false;
      const resources = current.resources.map((resource) => {
        if (String(resource.resourceId ?? '').trim() !== normalizedId) return resource;
        changed = true;
        return { ...resource, elementName: params.name };
      });
      return changed ? { ...current, resources } : current;
    }
  );
}
